"use client";

import { App } from "@capacitor/app";
import { Capacitor, type PermissionState } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { PushNotifications } from "@capacitor/push-notifications";
import { useCallback, useEffect, useRef, useState } from "react";
import { getSession } from "@/actions/auth";
import {
  registerDevice,
  unregisterDevice,
} from "@/actions/push-device-registration";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { readBrowserSafeEnv } from "@/lib/browser-safe-env";
import { createClientLogger } from "@/lib/logging/client";
import {
  ensureNativePushChannels,
  LEGACY_ANDROID_CHANNEL_ID,
} from "@/lib/native/hiive-push-bridge";
import {
  getNativePushStatus,
  markNativePushRequested,
  type NativePushAuthorizationStatus,
  requestNativeFullPush,
  requestNativeProvisionalPush,
} from "@/lib/native/hiive-push-permissions";
import { PushTokenSync } from "@/lib/native/push-token-sync";

const logger = createClientLogger("hooks/use-push-notifications");

const refreshedNativeTokens = new PushTokenSync(async (token, accountId) => {
  const session = await getSession();
  if (session?.user?.id !== accountId) return true; // obsolete account; never reassign
  const result = await registerDevice({
    platform: Capacitor.getPlatform() as "ios" | "android",
    token,
    expectedUserId: accountId,
    userAgent: navigator.userAgent,
  });
  return result.ok;
});
let nativePushRegistrationSuspended = false;

/**
 * Push is the main re-engagement lever, and until now none of it was
 * measurable: prompt → grant → token → delivery → tap had no events at any
 * stage, so opt-in rate and notification-driven return visits were both
 * unknowable.
 */
function trackPushOutcome(
  event:
    | typeof ANALYTICS_EVENTS.PUSH_NOTIFICATION_PERMISSION_REQUESTED
    | typeof ANALYTICS_EVENTS.PUSH_NOTIFICATION_PROVISIONAL_GRANTED
    | typeof ANALYTICS_EVENTS.PUSH_NOTIFICATION_PERMISSION_GRANTED
    | typeof ANALYTICS_EVENTS.PUSH_NOTIFICATION_PERMISSION_DENIED
    | typeof ANALYTICS_EVENTS.PUSH_NOTIFICATION_TOKEN_REGISTERED
    | typeof ANALYTICS_EVENTS.PUSH_NOTIFICATION_REGISTRATION_FAILED,
  properties?: Record<string, unknown>,
) {
  captureClientEvent(event, {
    transport: Capacitor.isNativePlatform() ? "native" : "web",
    platform: Capacitor.getPlatform(),
    ...properties,
  });
}

type PermissionStatus = PermissionState | "default";

export type PushSubscribeErrorCode =
  | "INSTALL_REQUIRED"
  | "BRAVE_PUSH_BACKEND_DISABLED"
  | "PUSH_PERMISSION_DENIED"
  | "NATIVE_PERMISSION_DENIED_IOS"
  | "NATIVE_PERMISSION_DENIED_ANDROID"
  | "NATIVE_REGISTRATION_FAILED"
  | "SW_READY_TIMEOUT"
  | "PUSH_SUBSCRIBE_FAILED"
  | "PUSH_NOT_SUPPORTED"
  | "DEVICE_REGISTER_FAILED";

/**
 * What the app may do about notifications for this user, right now.
 *
 * `provisional` and `needs_registration` are the two states the previous union
 * could not express, and both were being reported as something they are not:
 *
 * - A provisionally authorized iOS user reads as `granted` through Capacitor,
 *   so they used to land in `enabled` and the nudge kit showed them nothing —
 *   despite being the exact population it exists to reach.
 * - A user whose permission is granted but whose device row failed to write
 *   used to land in `can_enable`, so the CTA re-registered silently with no
 *   dialog. That is a repair, not an ask, and it must never be offered as one.
 *
 * `install_required` is gone: it was declared but never computed, and with
 * nudges scoped to native it has no future caller.
 */
export type PushNotificationAvailability =
  | "loading"
  | "enabled"
  | "provisional"
  | "can_prompt"
  | "needs_registration"
  | "permission_blocked"
  | "unsupported";

export class PushSubscribeError extends Error {
  readonly code: PushSubscribeErrorCode;
  readonly cause?: unknown;

  constructor(code: PushSubscribeErrorCode, message: string, cause?: unknown) {
    super(message);
    this.name = "PushSubscribeError";
    this.code = code;
    this.cause = cause;
  }
}

const NATIVE_PUSH_TIMEOUT_MS = 12_000;
const WEB_PUSH_TIMEOUT_MS = 12_000;

let sharedNativeRegistrationPromise: Promise<string> | null = null;
let sharedNativeToken: string | null = null;

const isPromptLike = (status: PermissionStatus): boolean =>
  status === "default" ||
  status === "prompt" ||
  status === "prompt-with-rationale";

const combineNativePermissionState = (
  pushPermission: PermissionState,
  localPermission: PermissionState,
): PermissionStatus => {
  if (pushPermission === "denied" || localPermission === "denied") {
    return "denied";
  }

  if (pushPermission === "granted" && localPermission === "granted") {
    return "granted";
  }

  if (
    pushPermission === "prompt-with-rationale" ||
    localPermission === "prompt-with-rationale"
  ) {
    return "prompt-with-rationale";
  }

  return "prompt";
};

/**
 * Android notification channels.
 *
 * "hiive-default" is still created here: pre-split binaries post everything
 * to it, and Capacitor uses it for any FCM `notification` payload. On a
 * binary with the split it is only the catch-all, so it is labelled "Other
 * notifications" (matching the native definition); on an older one it is
 * still every notification, so it keeps "Hiive Notifications".
 *
 * The per-type channels (Messages, Bookings & reminders, Activity,
 * Promotions) are created natively via HiivePushBridge.ensureChannels — the
 * one place they are defined, because Android freezes a channel's importance
 * on first creation. On a binary without that method this is a no-op and
 * everything stays on "hiive-default".
 */
const ensureAndroidNotificationChannels = async () => {
  if (Capacitor.getPlatform() !== "android") return;

  const split = (await ensureNativePushChannels()).size > 0;
  const channel = {
    id: LEGACY_ANDROID_CHANNEL_ID,
    name: split ? "Other notifications" : "Hiive Notifications",
    description: split
      ? "Notifications not covered by the other Hiive categories"
      : "General notifications and reminders",
    importance: 4 as const,
    visibility: 1 as const,
    lights: true,
    vibration: true,
  };

  await Promise.allSettled([
    PushNotifications.createChannel(channel),
    LocalNotifications.createChannel(channel),
  ]);
};

const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutError: () => Error,
): Promise<T> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(timeoutError()), timeoutMs);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });

const getWebPermissionState = (): PermissionStatus => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "default";
  }

  return Notification.permission as PermissionStatus;
};

const isWebPushSupported = (): boolean =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window &&
  window.isSecureContext;

const isIosBrowser = (): boolean => {
  if (typeof navigator === "undefined") return false;

  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

const isInstalledWebApp = (): boolean => {
  if (typeof window === "undefined") return false;

  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
};

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }

  return output;
};

const arrayBufferToBase64Url = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";

  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
};

const getWebPushKeys = (subscription: PushSubscription) => {
  const p256dh = subscription.getKey("p256dh");
  const auth = subscription.getKey("auth");

  if (!p256dh || !auth) {
    throw new PushSubscribeError(
      "PUSH_SUBSCRIBE_FAILED",
      "Browser push subscription did not include required encryption keys.",
    );
  }

  return {
    p256dh: arrayBufferToBase64Url(p256dh),
    auth: arrayBufferToBase64Url(auth),
  };
};

const normalizeUnknownError = (error: unknown): Error =>
  error instanceof Error
    ? error
    : new Error("Failed to enable notifications. Please try again.");

const normalizeNativePushError = (error: unknown): PushSubscribeError => {
  if (error instanceof PushSubscribeError) return error;

  const err = normalizeUnknownError(error);
  return new PushSubscribeError(
    "NATIVE_REGISTRATION_FAILED",
    err.message || "Failed to register this device for notifications.",
    err,
  );
};

const normalizeWebPushError = (error: unknown): PushSubscribeError => {
  if (error instanceof PushSubscribeError) return error;

  const err = normalizeUnknownError(error);
  const message = err.message || "Failed to enable browser notifications.";
  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes("push service")) {
    return new PushSubscribeError("BRAVE_PUSH_BACKEND_DISABLED", message, err);
  }

  return new PushSubscribeError("PUSH_SUBSCRIBE_FAILED", message, err);
};

export interface UsePushNotificationsReturn {
  isSupported: boolean;
  permission: PermissionStatus;
  isRegistered: boolean;
  isLoading: boolean;
  availability: PushNotificationAvailability;
  /** The real iOS authorization status, which Capacitor alone cannot report. */
  nativeAuthStatus: NativePushAuthorizationStatus | null;
  /**
   * Whether a system dialog could still appear for this user.
   *
   * The nudge kit must never render a primary CTA when this is false: iOS
   * allows one alert per install and Android blocks permanently after two
   * dismissals, so past that point `requestPermission` resolves without
   * anything appearing on screen and the tap reads as broken.
   */
  canPromptOs: boolean;
  canEnableInApp: boolean;
  requestPermission: () => Promise<void>;
  /**
   * Grants quietly on iOS without showing anything. Used by onboarding, which
   * keeps its ask but must not spend the one alert before the user has booked
   * anything. A no-op everywhere else.
   */
  requestProvisional: () => Promise<void>;
  unregister: () => Promise<void>;
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<PermissionStatus>("default");
  const [isRegistered, setIsRegistered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [nativeAuthStatus, setNativeAuthStatus] =
    useState<NativePushAuthorizationStatus | null>(null);
  const [canPromptOs, setCanPromptOs] = useState(false);
  const nativeTokenRef = useRef<string | null>(null);
  const webTokenRef = useRef<string | null>(null);
  const nativeRegistrationWaitersRef = useRef<
    Array<{
      resolve: () => void;
      reject: (error: PushSubscribeError) => void;
      timeoutId: ReturnType<typeof setTimeout>;
    }>
  >([]);

  const isNative = Capacitor.isNativePlatform();

  const resolveNativeRegistrationWaiters = useCallback(() => {
    const pending = nativeRegistrationWaitersRef.current;
    nativeRegistrationWaitersRef.current = [];
    for (const waiter of pending) {
      clearTimeout(waiter.timeoutId);
      waiter.resolve();
    }
  }, []);

  const rejectNativeRegistrationWaiters = useCallback(
    (error: PushSubscribeError) => {
      const pending = nativeRegistrationWaitersRef.current;
      nativeRegistrationWaitersRef.current = [];
      for (const waiter of pending) {
        clearTimeout(waiter.timeoutId);
        waiter.reject(error);
      }
    },
    [],
  );

  const refreshPermissionState = useCallback(async () => {
    try {
      if (!isNative) {
        const supported = isWebPushSupported();
        const webPermission = getWebPermissionState();
        setIsSupported(supported);
        setPermission(webPermission);

        if (!supported || webPermission !== "granted") {
          setIsRegistered(false);
          return;
        }

        const registration = await withTimeout(
          navigator.serviceWorker.ready,
          WEB_PUSH_TIMEOUT_MS,
          () =>
            new PushSubscribeError(
              "SW_READY_TIMEOUT",
              "Timed out while waiting for the browser notification service.",
            ),
        );
        const subscription = await registration.pushManager.getSubscription();
        webTokenRef.current = subscription?.endpoint ?? null;
        setIsRegistered(Boolean(subscription));
        return;
      }

      setIsSupported(true);
      // Capacitor's own check is kept because registration still hangs off it,
      // but it cannot see `.provisional` — it reports that as `granted` — so the
      // real status comes from the Hiive bridge alongside it.
      const [pushPermStatus, localPermStatus, nativeStatus] = await Promise.all(
        [
          PushNotifications.checkPermissions(),
          LocalNotifications.checkPermissions(),
          getNativePushStatus(),
        ],
      );
      setNativeAuthStatus(nativeStatus.status);
      setCanPromptOs(nativeStatus.canRequestFull);
      const combinedPermission = combineNativePermissionState(
        pushPermStatus.receive,
        localPermStatus.display,
      );
      setPermission(combinedPermission);
      if (combinedPermission !== "granted") {
        setIsRegistered(false);
      } else if (nativeTokenRef.current || sharedNativeToken) {
        setIsRegistered(true);
      }
    } catch (error) {
      logger.warn("Push notifications refresh error:", error);
    }
  }, [isNative]);

  const waitForNativeRegistrationResult = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(
            new PushSubscribeError(
              "NATIVE_REGISTRATION_FAILED",
              "Timed out while registering this device for notifications.",
            ),
          );
        }, NATIVE_PUSH_TIMEOUT_MS);

        nativeRegistrationWaitersRef.current.push({
          resolve,
          reject,
          timeoutId,
        });
      }),
    [],
  );

  const registerNativeDevice = useCallback(async () => {
    nativePushRegistrationSuspended = false;
    refreshedNativeTokens.resume();
    if (sharedNativeRegistrationPromise) {
      const token = await sharedNativeRegistrationPromise;
      nativeTokenRef.current = token;
      setIsRegistered(true);
      return token;
    }

    const session = await getSession();
    if (!session?.user?.id) {
      throw new PushSubscribeError(
        "NATIVE_REGISTRATION_FAILED",
        "Must be logged in to enable notifications.",
      );
    }

    sharedNativeRegistrationPromise = (async () => {
      const registrationListener = await PushNotifications.addListener(
        "registration",
        async (token) => {
          try {
            sharedNativeToken = token.value;
            nativeTokenRef.current = token.value;

            const platform = Capacitor.getPlatform() as "ios" | "android";
            logger.info("push.native_registration.succeeded", {
              platform,
              token_present: Boolean(token.value),
            });
            if (nativePushRegistrationSuspended) return;
            const result = await registerDevice({
              expectedUserId: session.user.id,
              platform,
              token: token.value,
              userAgent: navigator.userAgent,
            });

            if (!result.ok) {
              rejectNativeRegistrationWaiters(
                new PushSubscribeError(
                  "NATIVE_REGISTRATION_FAILED",
                  result.message || "Failed to register this device.",
                ),
              );
              return;
            }

            setIsRegistered(true);
            resolveNativeRegistrationWaiters();
          } catch (error) {
            setIsRegistered(false);
            rejectNativeRegistrationWaiters(normalizeNativePushError(error));
          }
        },
      );

      const registrationErrorListener = await PushNotifications.addListener(
        "registrationError",
        (error) => {
          logger.warn("Push registration error:", error.error);
          setIsRegistered(false);
          rejectNativeRegistrationWaiters(
            new PushSubscribeError(
              "NATIVE_REGISTRATION_FAILED",
              error.error || "Native push registration failed.",
            ),
          );
        },
      );

      try {
        const nativeRegistrationPromise = waitForNativeRegistrationResult();
        await PushNotifications.register();
        await nativeRegistrationPromise;

        if (!sharedNativeToken) {
          throw new PushSubscribeError(
            "NATIVE_REGISTRATION_FAILED",
            "Native registration finished without returning a push token.",
          );
        }

        return sharedNativeToken;
      } finally {
        await Promise.allSettled([
          registrationListener.remove(),
          registrationErrorListener.remove(),
        ]);
        sharedNativeRegistrationPromise = null;
      }
    })();

    const token = await sharedNativeRegistrationPromise;
    nativeTokenRef.current = token;
    setIsRegistered(true);
    return token;
  }, [
    rejectNativeRegistrationWaiters,
    resolveNativeRegistrationWaiters,
    waitForNativeRegistrationResult,
  ]);

  const registerWebSubscription = useCallback(
    async (subscription: PushSubscription) => {
      const keys = getWebPushKeys(subscription);
      const result = await registerDevice({
        platform: "web",
        token: subscription.endpoint,
        webPushKeys: keys,
        userAgent: navigator.userAgent,
      });

      if (!result.ok) {
        throw new PushSubscribeError(
          "DEVICE_REGISTER_FAILED",
          result.message ||
            "Failed to register this browser for notifications.",
        );
      }

      webTokenRef.current = subscription.endpoint;
      setIsRegistered(true);
      return subscription.endpoint;
    },
    [],
  );

  const registerWebDevice = useCallback(async () => {
    const session = await getSession();
    if (!session?.user?.id) {
      throw new PushSubscribeError(
        "DEVICE_REGISTER_FAILED",
        "Must be logged in to enable notifications.",
      );
    }

    const vapidPublicKey = readBrowserSafeEnv(
      "NEXT_PUBLIC_VAPID_PUBLIC_KEY",
    )?.trim();
    if (!vapidPublicKey) {
      throw new PushSubscribeError(
        "PUSH_SUBSCRIBE_FAILED",
        "Browser push public key is not configured.",
      );
    }

    const registration = await withTimeout(
      navigator.serviceWorker.ready,
      WEB_PUSH_TIMEOUT_MS,
      () =>
        new PushSubscribeError(
          "SW_READY_TIMEOUT",
          "Timed out while waiting for the browser notification service.",
        ),
    );
    const existingSubscription =
      await registration.pushManager.getSubscription();
    const subscription =
      existingSubscription ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      }));

    return registerWebSubscription(subscription);
  }, [registerWebSubscription]);

  useEffect(() => {
    const init = async () => {
      try {
        if (!isNative) {
          const supported = isWebPushSupported();
          const webPermission = getWebPermissionState();
          setIsSupported(supported);
          setPermission(webPermission);

          if (!supported || webPermission !== "granted") {
            setIsRegistered(false);
            return;
          }

          const session = await getSession();
          if (!session?.user?.id) {
            setIsRegistered(false);
            return;
          }

          const registration = await withTimeout(
            navigator.serviceWorker.ready,
            WEB_PUSH_TIMEOUT_MS,
            () =>
              new PushSubscribeError(
                "SW_READY_TIMEOUT",
                "Timed out while waiting for the browser notification service.",
              ),
          );
          const subscription = await registration.pushManager.getSubscription();
          if (!subscription) {
            setIsRegistered(false);
            return;
          }

          await registerWebSubscription(subscription);
          return;
        }

        setIsSupported(true);

        const [pushPermStatus, localPermStatus, nativeStatus] =
          await Promise.all([
            PushNotifications.checkPermissions(),
            LocalNotifications.checkPermissions(),
            getNativePushStatus(),
          ]);

        setNativeAuthStatus(nativeStatus.status);
        setCanPromptOs(nativeStatus.canRequestFull);

        const combinedPermission = combineNativePermissionState(
          pushPermStatus.receive,
          localPermStatus.display,
        );

        setPermission(combinedPermission);
        if (combinedPermission !== "granted") {
          setIsRegistered(false);
        } else if (sharedNativeToken) {
          nativeTokenRef.current = sharedNativeToken;
        }

        if (combinedPermission === "granted") {
          await ensureAndroidNotificationChannels();
          const session = await getSession();
          if (session?.user?.id) {
            await registerNativeDevice();
          } else {
            setIsRegistered(false);
          }
        }
      } catch (error) {
        logger.warn("Push notifications init error:", error);
      } finally {
        setIsLoading(false);
        setHasInitialized(true);
      }
    };

    init();

    return () => {
      if (!isNative) return;

      rejectNativeRegistrationWaiters(
        new PushSubscribeError(
          "NATIVE_REGISTRATION_FAILED",
          "Push registration listener was removed.",
        ),
      );
    };
  }, [
    isNative,
    rejectNativeRegistrationWaiters,
    registerNativeDevice,
    registerWebSubscription,
  ]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        void refreshPermissionState();
      }
    };

    document.addEventListener("visibilitychange", refreshWhenVisible);

    let appListener: { remove: () => Promise<void> } | null = null;
    if (isNative) {
      void App.addListener("resume", () => {
        void refreshPermissionState();
      }).then((listener) => {
        appListener = listener;
      });
    }

    return () => {
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      void appListener?.remove();
    };
  }, [isNative, refreshPermissionState]);

  useEffect(() => {
    if (!isNative) return;
    let cancelled = false;
    let listener: { remove: () => Promise<void> } | undefined;
    let retryTimer: ReturnType<typeof setInterval> | undefined;
    void PushNotifications.addListener("registration", async ({ value }) => {
      if (cancelled || nativePushRegistrationSuspended) return;
      const session = await getSession();
      if (cancelled || nativePushRegistrationSuspended || !session?.user?.id)
        return;
      sharedNativeToken = value;
      nativeTokenRef.current = value;
      await refreshedNativeTokens.offer(value, session.user.id);
    }).then((handle) => {
      if (cancelled) void handle.remove();
      else listener = handle;
    });
    const retry = () => {
      if (!nativePushRegistrationSuspended) void refreshedNativeTokens.flush();
    };
    window.addEventListener("online", retry);
    retryTimer = setInterval(retry, 30_000);
    return () => {
      cancelled = true;
      void listener?.remove();
      clearInterval(retryTimer);
      window.removeEventListener("online", retry);
    };
  }, [isNative]);

  const requestPermission = useCallback(async () => {
    trackPushOutcome(ANALYTICS_EVENTS.PUSH_NOTIFICATION_PERMISSION_REQUESTED);

    if (!isNative) {
      if (!isWebPushSupported()) {
        throw new PushSubscribeError(
          "PUSH_NOT_SUPPORTED",
          "Browser push notifications require a supported browser on a secure connection.",
        );
      }

      if (isIosBrowser() && !isInstalledWebApp()) {
        throw new PushSubscribeError(
          "INSTALL_REQUIRED",
          "Install Hiive to your Home Screen before enabling browser notifications on iOS.",
        );
      }

      setIsLoading(true);
      try {
        const nextPermission =
          Notification.permission === "granted"
            ? "granted"
            : await Notification.requestPermission();
        setPermission(nextPermission as PermissionStatus);

        if (nextPermission !== "granted") {
          setIsRegistered(false);
          trackPushOutcome(
            ANALYTICS_EVENTS.PUSH_NOTIFICATION_PERMISSION_DENIED,
            { permission: nextPermission },
          );
          throw new PushSubscribeError(
            "PUSH_PERMISSION_DENIED",
            "Browser notifications are blocked for Hiive.",
          );
        }

        trackPushOutcome(ANALYTICS_EVENTS.PUSH_NOTIFICATION_PERMISSION_GRANTED);
        await registerWebDevice();
        trackPushOutcome(ANALYTICS_EVENTS.PUSH_NOTIFICATION_TOKEN_REGISTERED);
      } catch (error) {
        const normalized = normalizeWebPushError(error);
        logger.warn("Failed to request web push permission:", normalized);
        trackPushOutcome(
          ANALYTICS_EVENTS.PUSH_NOTIFICATION_REGISTRATION_FAILED,
          { error_code: normalized.code, error_message: normalized.message },
        );
        throw normalized;
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!isSupported) {
      throw new PushSubscribeError(
        "PUSH_NOT_SUPPORTED",
        "Push notifications are not supported in this environment.",
      );
    }

    setIsLoading(true);
    try {
      const session = await getSession();
      if (!session?.user?.id) {
        throw new PushSubscribeError(
          "NATIVE_REGISTRATION_FAILED",
          "Must be logged in to enable notifications.",
        );
      }

      const platform = Capacitor.getPlatform();
      const nativePermissionDeniedCode =
        platform === "ios"
          ? "NATIVE_PERMISSION_DENIED_IOS"
          : "NATIVE_PERMISSION_DENIED_ANDROID";

      /*
       * The provisional upgrade cannot go through Capacitor.
       *
       * `checkPermissions()` reports a provisional user as `granted`, so the
       * `isPromptLike` guard below is false and the request short-circuits —
       * the user would be silently re-registered and no dialog would ever
       * appear, while the CTA claimed to be turning notifications on.
       *
       * Asking WITHOUT `.provisional` from a provisional state is the documented
       * way to raise the real alert, and the bridge is the only thing that can
       * make that call.
       */
      if (nativeAuthStatus === "provisional") {
        const upgraded = await requestNativeFullPush();
        setNativeAuthStatus(upgraded.status);
        setCanPromptOs(upgraded.canRequestFull);

        if (upgraded.status !== "authorized") {
          trackPushOutcome(
            ANALYTICS_EVENTS.PUSH_NOTIFICATION_PERMISSION_DENIED,
            { permission: upgraded.status, upgraded_from: "provisional" },
          );
          throw new PushSubscribeError(
            "NATIVE_PERMISSION_DENIED_IOS",
            "Enable notifications in iOS Settings > Notifications > Hiive.",
          );
        }

        trackPushOutcome(
          ANALYTICS_EVENTS.PUSH_NOTIFICATION_PERMISSION_GRANTED,
          { upgraded_from: "provisional" },
        );
        setPermission("granted");
        await registerNativeDevice();
        trackPushOutcome(ANALYTICS_EVENTS.PUSH_NOTIFICATION_TOKEN_REGISTERED);
        return;
      }

      let [pushPermStatus, localPermStatus] = await Promise.all([
        PushNotifications.checkPermissions(),
        LocalNotifications.checkPermissions(),
      ]);

      if (isPromptLike(pushPermStatus.receive)) {
        // Android reports nothing that separates "never asked" from "asked
        // twice and permanently blocked" — both answer false to
        // shouldShowRequestPermissionRationale. Recording the ask here is what
        // lets a later read tell a fresh install from a dead one.
        if (platform === "android") await markNativePushRequested();
        pushPermStatus = await withTimeout(
          PushNotifications.requestPermissions(),
          NATIVE_PUSH_TIMEOUT_MS,
          () =>
            new PushSubscribeError(
              nativePermissionDeniedCode,
              "Notification permission request timed out.",
            ),
        );
      }

      if (pushPermStatus.receive === "denied") {
        localPermStatus = { display: "denied" };
      } else if (isPromptLike(localPermStatus.display)) {
        localPermStatus = await withTimeout(
          LocalNotifications.requestPermissions(),
          NATIVE_PUSH_TIMEOUT_MS,
          () =>
            new PushSubscribeError(
              nativePermissionDeniedCode,
              "Notification permission request timed out.",
            ),
        );
      }

      const combinedPermission = combineNativePermissionState(
        pushPermStatus.receive,
        localPermStatus.display,
      );

      setPermission(combinedPermission);

      if (combinedPermission !== "granted") {
        setIsRegistered(false);
        trackPushOutcome(ANALYTICS_EVENTS.PUSH_NOTIFICATION_PERMISSION_DENIED, {
          permission: combinedPermission,
          push_permission: pushPermStatus.receive,
          local_permission: localPermStatus.display,
        });
        if (platform === "ios") {
          throw new PushSubscribeError(
            "NATIVE_PERMISSION_DENIED_IOS",
            "Enable notifications in iOS Settings > Notifications > Hiive.",
          );
        }

        throw new PushSubscribeError(
          "NATIVE_PERMISSION_DENIED_ANDROID",
          "Enable notifications in Android Settings > Apps > Hiive > Notifications.",
        );
      }

      trackPushOutcome(ANALYTICS_EVENTS.PUSH_NOTIFICATION_PERMISSION_GRANTED);
      await ensureAndroidNotificationChannels();
      await registerNativeDevice();
      trackPushOutcome(ANALYTICS_EVENTS.PUSH_NOTIFICATION_TOKEN_REGISTERED);
    } catch (error) {
      const normalized = normalizeNativePushError(error);
      logger.warn("Failed to request push permission:", normalized);
      trackPushOutcome(ANALYTICS_EVENTS.PUSH_NOTIFICATION_REGISTRATION_FAILED, {
        error_code: normalized.code,
        error_message: normalized.message,
      });
      throw normalized;
    } finally {
      setIsLoading(false);
    }
  }, [
    isNative,
    isSupported,
    registerNativeDevice,
    registerWebDevice,
    nativeAuthStatus,
  ]);

  /**
   * The quiet ask. Grants without showing anything on iOS; a no-op elsewhere.
   *
   * This is what onboarding calls. The ask stays exactly where it was — the
   * screen, the copy and the button are unchanged — but it no longer spends the
   * single alert iOS allows per install at the moment the user has least reason
   * to say yes. Notifications simply start arriving in Notification Center,
   * carrying their own Keep / Turn Off buttons, and the real dialog stays
   * available for a moment that has earned it.
   */
  const requestProvisional = useCallback(async () => {
    if (!isNative) return;

    try {
      const status = await requestNativeProvisionalPush();
      setNativeAuthStatus(status.status);
      setCanPromptOs(status.canRequestFull);

      if (status.status !== "provisional" && status.status !== "authorized") {
        return;
      }

      trackPushOutcome(ANALYTICS_EVENTS.PUSH_NOTIFICATION_PROVISIONAL_GRANTED, {
        status: status.status,
      });

      setPermission("granted");
      await ensureAndroidNotificationChannels();
      const session = await getSession();
      if (!session?.user?.id) return;
      await registerNativeDevice();
      trackPushOutcome(ANALYTICS_EVENTS.PUSH_NOTIFICATION_TOKEN_REGISTERED);
    } catch (error) {
      // Provisional is an optimisation on top of a screen that already works.
      // A binary predating the plugin, or any failure inside it, must not block
      // onboarding from continuing.
      logger.warn("Provisional push authorization unavailable:", error);
    }
  }, [isNative, registerNativeDevice]);

  const unregister = useCallback(async () => {
    nativePushRegistrationSuspended = true;
    refreshedNativeTokens.suspend();
    if (!isNative) {
      if (!isWebPushSupported()) {
        setIsRegistered(false);
        return;
      }

      setIsLoading(true);
      try {
        const registration = await withTimeout(
          navigator.serviceWorker.ready,
          WEB_PUSH_TIMEOUT_MS,
          () =>
            new PushSubscribeError(
              "SW_READY_TIMEOUT",
              "Timed out while waiting for the browser notification service.",
            ),
        );
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          await unregisterDevice(subscription.endpoint);
        } else if (webTokenRef.current) {
          await unregisterDevice(webTokenRef.current);
        }

        webTokenRef.current = null;
        setIsRegistered(false);
      } catch (error) {
        logger.warn("Failed to unregister browser push:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    try {
      await PushNotifications.unregister();
      const token = nativeTokenRef.current ?? sharedNativeToken;
      if (token) {
        await unregisterDevice(token);
        nativeTokenRef.current = null;
        sharedNativeToken = null;
        sharedNativeRegistrationPromise = null;
      }
      setIsRegistered(false);
    } catch (error) {
      logger.warn("Failed to unregister from push:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isNative]);

  /**
   * Resolution order matters, and it is not the obvious one.
   *
   * `provisional` is tested BEFORE `isRegistered`, because a provisional user is
   * fully registered and receiving notifications — quietly. Checking
   * registration first (which is what the previous version did, by necessity)
   * classified them as `enabled` and hid every nudge from the one audience that
   * still has something to gain.
   *
   * `needs_registration` is tested before `can_prompt` for the mirror reason: a
   * granted user without a device row needs a silent repair, and offering them
   * a "Turn on notifications" button would fire no dialog at all.
   */
  const availability: PushNotificationAvailability = !hasInitialized
    ? "loading"
    : !isSupported
      ? "unsupported"
      : nativeAuthStatus === "provisional"
        ? "provisional"
        : permission === "denied" || nativeAuthStatus === "denied"
          ? "permission_blocked"
          : isRegistered
            ? "enabled"
            : permission === "granted"
              ? "needs_registration"
              : "can_prompt";

  return {
    isSupported,
    permission,
    isRegistered,
    isLoading,
    availability,
    nativeAuthStatus,
    // On web the bridge reports nothing, so fall back to the browser's own
    // rule: one prompt while permission is still "default".
    canPromptOs: isNative ? canPromptOs : permission === "default",
    canEnableInApp:
      availability === "can_prompt" || availability === "provisional",
    requestPermission,
    requestProvisional,
    unregister,
  };
}
